// src/components/Products/Products.jsx
import { useMemo, useEffect, memo } from 'react';
import Product from './Product';
import { useParams } from 'react-router-dom';
import { useConfig } from "../../contexts/ConfigContext";
import { useAppState } from "../../contexts/AppStateContext";
import { getPriceFilteredProducts, consoleDebug, getPrioritySpecialForProduct } from '../../util.js';

const Products = ({
    products,
    display = null,
    setDisplayStyle,
    initialDisplayStyle,
    selectedFilters = null,
    menuSlug,
    setSelectedFilters,
    specialProductsIds,
    isRelatedProducts,
    specialsExist,
    columns,
    paginationLimit = null,
    isMenu = false,
    pricingType = null,
    specialsList,
    productSpecialMap,
    showMultipleSpecials = false,
}) => {
    const params = useParams();
    const { retailerId } = useAppState();
    const { surfside, analytics, retailers } = useConfig();

    // Compute menuSlug from context if not provided
    const computedMenuSlug = menuSlug || retailers[retailerId]?.menuSlug;

    // Preload first product image for better LCP
    useEffect(() => {
        // Get the first product with an image
        const firstProduct = products?.find(p => p?.images?.[0]?.url);
        if (!firstProduct?.images?.[0]?.url) return;

        const imageUrl = firstProduct.images[0].url;
        const supportsWebP = imageUrl.includes('dutchie.com');
        const preloadUrl = supportsWebP ? `${imageUrl}?w=250&fm=webp` : `${imageUrl}?w=250`;

        // Check if preload already exists
        const existingPreload = document.querySelector(`link[rel="preload"][href="${preloadUrl}"]`);
        if (existingPreload) return;

        // Create and add preload link
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = preloadUrl;
        link.type = supportsWebP ? 'image/webp' : 'image/jpeg';
        link.fetchPriority = 'high';

        document.head.appendChild(link);

        // Cleanup on unmount or when products change
        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, [products]);

    // Pre-compute special mappings for all products
    const productSpecialMapping = useMemo(() => {
        if (!products || products.length === 0) return new Map();

        const mapping = new Map();

        products.forEach(product => {
            if (!product?.id) return;

            if (showMultipleSpecials) {
                // Show all specials for this product
                if (productSpecialMap instanceof Map) {
                    const specials = productSpecialMap.get(product.id);
                    if (specials && specials.length > 0) {
                        mapping.set(product.id, specials); // Store all specials
                    }
                } else {
                    // Fallback: find all specials for this product
                    const matchingSpecials = [];
                    if (specialProductsIds && specialsList) {
                        for (const specialId in specialProductsIds) {
                            if (specialProductsIds[specialId] && specialProductsIds[specialId].includes(product.id)) {
                                const special = specialsList.find(s => s.id === specialId);
                                if (special) {
                                    matchingSpecials.push(special);
                                }
                            }
                        }
                    }
                    if (matchingSpecials.length > 0) {
                        // Sort by priority (newest first)
                        matchingSpecials.sort((a, b) => {
                            const aStart = a?.scheduleConfiguration?.startStamp ? new Date(a.scheduleConfiguration.startStamp) : null;
                            const bStart = b?.scheduleConfiguration?.startStamp ? new Date(b.scheduleConfiguration.startStamp) : null;
                            if (!aStart && !bStart) return 0;
                            if (!aStart) return 1;
                            if (!bStart) return -1;
                            return bStart - aStart;
                        });
                        mapping.set(product.id, matchingSpecials);
                    }
                }
            } else {
                // Original logic: show only priority special
                let prioritySpecial = null;

                if (productSpecialMap instanceof Map) {
                    const specials = productSpecialMap.get(product.id);
                    prioritySpecial = specials?.[0] || null;
                } else {
                    prioritySpecial = getPrioritySpecialForProduct(
                        product.id,
                        specialsList,
                        specialProductsIds
                    );
                }

                if (prioritySpecial) {
                    mapping.set(product.id, prioritySpecial);
                }
            }
        });

        consoleDebug('🏷️ Product special mapping created', {
            totalProducts: products.length,
            productsWithSpecials: mapping.size,
            showMultipleSpecials
        });

        return mapping;
    }, [products, specialsList, specialProductsIds, productSpecialMap, showMultipleSpecials]);

    if (
        isMenu &&
        pricingType &&
        selectedFilters &&
        selectedFilters.price &&
        (selectedFilters.price.min || selectedFilters.price.max)
    ) {
        products = getPriceFilteredProducts(
            products,
            selectedFilters.price,
            pricingType,
            paginationLimit
        );
    }

    // If no products and this is a menu query, return null
    // Menu.jsx handles "no products" messaging with proper loading state management
    if (products.length === 0 && isMenu) {
        return null;
    }

    return (
        <>
            {products.length === 0 ? (
                <p className="headless-no-products">
                    Sorry, no products were found! Please try refining your search, or use the filter options to narrow down the products.
                </p>
            ) : (
                <>
                    {products?.map((product, i) => {
                        const specialsData = product?.id
                            ? productSpecialMapping.get(product.id) || null
                            : null;

                        // Handle backward compatibility
                        const prioritySpecialObject = showMultipleSpecials
                            ? (Array.isArray(specialsData) ? specialsData[0] : specialsData)  // First special for backward compatibility
                            : specialsData;

                        const allSpecials = showMultipleSpecials && Array.isArray(specialsData)
                            ? specialsData
                            : null;

                        return product ? (
                            <Product
                                prioritySpecialObject={prioritySpecialObject}
                                allSpecials={allSpecials}
                                showMultipleSpecials={showMultipleSpecials}
                                key={product.id + '-' + i}
                                product={product}
                                display={display}
                                setDisplayStyle={setDisplayStyle}
                                menuSlug={computedMenuSlug}
                                isRelatedProducts={isRelatedProducts}
                                columns={columns}
                                initialDisplayStyle={initialDisplayStyle}
                                isFirstProduct={i === 0}
                            />
                        ) : (
                            <div key={i}>Product not found!</div>
                        );
                    })}
                </>
            )}
        </>
    );
};

// Wrap in memo to prevent unnecessary re-renders
export default memo(Products);
